import { useCallback, useMemo } from 'react';
import { addDays, addYears, format, isAfter, max, startOfDay } from 'date-fns';
import { Callout, Column, KeyboardDatePicker, MenuItem, Row, Select, Typography } from 'seagull';
import { useTestIds } from 'seagull/utils/TestIds/getTestIds';
import { useIoContext } from "../../../contexts";
import { useServiceBillingDetails } from "../../../hooks/reactQuery/queries/useServiceBillingDetails";
import { serviceBillingModel, serviceModel } from "../../../models";
import { validateStartBillingAt } from "../../../models/service/logic";
import { pluralize } from "../../../utils/pluralize";
import { AdvancedSettingsControl } from "../AdvancedSettingsControl/AdvancedSettingsControl";
import { AdvancedSetting } from "../ServiceAdvancedSettings.types";
import styles from "./StartAutomaticBillingAtControl.module.scss";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export let StartAutomaticBillingAtControlDropdownMenuItem = /*#__PURE__*/function (StartAutomaticBillingAtControlDropdownMenuItem) {
  StartAutomaticBillingAtControlDropdownMenuItem["Pause"] = "Pause";
  StartAutomaticBillingAtControlDropdownMenuItem["SpecificDate"] = "SpecificDate";
  StartAutomaticBillingAtControlDropdownMenuItem["AcceptanceDate"] = "AcceptanceDate";
  return StartAutomaticBillingAtControlDropdownMenuItem;
}({});
export const StartAutomaticBillingAtControl = _ref => {
  let {
    isReadOnly,
    info,
    label,
    disabled,
    disabledTooltip,
    service,
    relationshipState,
    onPausedChange,
    onBillOnAcceptanceChange,
    onStartBillingAtChange,
    testId
  } = _ref;
  const {
    StartAutomaticBillingAtControlTestIds
  } = useTestIds(testId);
  const isPaused = useMemo(() => serviceBillingModel.isPaused(service.billing), [service]);
  const startBillingAt = serviceModel.startBillingAt(service);
  const isProrated = serviceModel.isProrated(service);
  const isAmendedService = serviceModel.isFromAmendment(service);
  const relationshipEffectiveDate = relationshipState.effectiveDate;
  const {
    nextBillingDate,
    periods
  } = useServiceBillingDetails({
    service,
    relationshipEffectiveDate
  });
  const shouldShowProrateWarning = useMemo(() => {
    if (!isProrated && startBillingAt && nextBillingDate) {
      return nextBillingDate.getTime() !== startBillingAt.getTime();
    }
    return false;
  }, [nextBillingDate, startBillingAt, isProrated]);
  const isBillOnPeriodBeforeNextBilling = serviceModel.isBillOnPeriodBeforeNextBilling(service);
  const isBilledUpfront = serviceModel.isBilledUpfront(service);
  const {
    getNow
  } = useIoContext();
  const getToday = useCallback(() => startOfDay(getNow()), []);
  const isRelationshipEffectiveDateIsInTheFuture = useMemo(() => {
    if (relationshipEffectiveDate) {
      return isAfter(startOfDay(relationshipEffectiveDate), getToday());
    }
    return false;
  }, [relationshipEffectiveDate, getToday]);
  const getAmendedMinDate = useCallback(() => {
    const tomorrowDate = addDays(getToday(), 1);
    return relationshipEffectiveDate ? max([tomorrowDate, relationshipEffectiveDate]) : tomorrowDate;
  }, [getToday, relationshipEffectiveDate]);
  const amendedMinDate = useMemo(() => getAmendedMinDate(), [getAmendedMinDate]);
  const getInitialEffectiveDate = useCallback(() => {
    if (isAmendedService) {
      return getAmendedMinDate();
    }
    if (relationshipEffectiveDate) {
      return max([startOfDay(relationshipEffectiveDate), getToday()]);
    }
    return getToday();
  }, [relationshipEffectiveDate, getToday, isAmendedService, getAmendedMinDate]);
  const minDate = useMemo(() => addYears(getToday(), -1), [getToday]);
  const selectedItem = useMemo(() => {
    if (isPaused) {
      return StartAutomaticBillingAtControlDropdownMenuItem.Pause;
    } else if (startBillingAt) {
      return StartAutomaticBillingAtControlDropdownMenuItem.SpecificDate;
    } else if (isBillOnPeriodBeforeNextBilling) {
      return StartAutomaticBillingAtControlDropdownMenuItem.AcceptanceDate;
    }
    return;
  }, [isPaused, isBillOnPeriodBeforeNextBilling, startBillingAt]);
  const isChecked = !!selectedItem;
  const {
    startBillingAtDropdownError,
    startBillingAtDatePickerError
  } = useMemo(() => {
    if (selectedItem !== StartAutomaticBillingAtControlDropdownMenuItem.SpecificDate || isReadOnly) {
      return {};
    }
    const error = validateStartBillingAt(service, {
      relationshipEffectiveDate,
      now: getToday()
    });
    const errorMessage = error[0];
    if (!errorMessage) {
      return {};
    }
    if (!relationshipEffectiveDate) {
      return {
        startBillingAtDropdownError: errorMessage
      };
    }
    return {
      startBillingAtDatePickerError: errorMessage
    };
  }, [selectedItem, startBillingAt, relationshipEffectiveDate, minDate]);
  const isStartBillingAtDateDisabled = isReadOnly || !relationshipEffectiveDate;
  const updateServiceByItem = useCallback((item, newValue) => {
    if (item === StartAutomaticBillingAtControlDropdownMenuItem.Pause) {
      return onPausedChange === null || onPausedChange === void 0 ? void 0 : onPausedChange(service.id, newValue);
    } else if (item === StartAutomaticBillingAtControlDropdownMenuItem.AcceptanceDate) {
      return onBillOnAcceptanceChange === null || onBillOnAcceptanceChange === void 0 ? void 0 : onBillOnAcceptanceChange(service.id, newValue);
    } else if (item === StartAutomaticBillingAtControlDropdownMenuItem.SpecificDate) {
      return onStartBillingAtChange === null || onStartBillingAtChange === void 0 ? void 0 : onStartBillingAtChange(service.id, newValue instanceof Date ? newValue : null);
    }
  }, [service, onPausedChange, onBillOnAcceptanceChange, onStartBillingAtChange]);
  const getOnAcceptanceText = useCallback(() => {
    if (isAmendedService) {
      return {
        value: StartAutomaticBillingAtControlDropdownMenuItem.AcceptanceDate,
        text: 'Issue an invoice immediately',
        subtext: 'An invoice for this service will be issued immediately once amendments are saved in addition to the invoices issued on the upcoming billing date and going forward. Invoices including this service will require an approval from your client until this amendment is approved.'
      };
    }
    if (isRelationshipEffectiveDateIsInTheFuture) {
      return {
        value: StartAutomaticBillingAtControlDropdownMenuItem.AcceptanceDate,
        text: 'Start billing on agreement effective date',
        subtext: 'The service will be charged on the effective date of the agreement (even if it’s not the set billing day) and continue issuing from the next scheduled billing date and going forward.'
      };
    }
    return {
      value: StartAutomaticBillingAtControlDropdownMenuItem.AcceptanceDate,
      text: 'Start billing on proposal acceptance',
      subtext: 'The service will be charged on the day the proposal is accepted (even if it’s not the set billing day) and continue issuing from the next scheduled billing date and going forward.'
    };
  }, [isRelationshipEffectiveDateIsInTheFuture, isAmendedService]);
  const menuItems = useMemo(() => {
    return [{
      value: StartAutomaticBillingAtControlDropdownMenuItem.SpecificDate,
      text: 'Start billing on a specific date',
      disabled: isStartBillingAtDateDisabled,
      tooltip: isStartBillingAtDateDisabled ? 'Start billing on a specific date isn’t available if the agreement effective date is “on acceptance.”' : undefined,
      subtext: 'Choose a start date for automatic billing of this service. If the date has already passed or the proposal is accepted after that date, invoices for prior periods will be issued automatically.'
    }, ...(isBilledUpfront ? [getOnAcceptanceText()] : []), {
      value: StartAutomaticBillingAtControlDropdownMenuItem.Pause,
      text: "Don't start automatic billing",
      subtext: 'The service will not be billed until you manually initiate automatic billing for it.'
    }];
  }, [isBilledUpfront, isStartBillingAtDateDisabled, getOnAcceptanceText]);
  const callouts = useMemo(() => {
    const _callouts = [];
    if (!isChecked) {
      return _callouts;
    }
    if (selectedItem === StartAutomaticBillingAtControlDropdownMenuItem.SpecificDate && startBillingAt) {
      if (isAfter(startBillingAt, getToday())) {
        _callouts.push({
          dataTestid: StartAutomaticBillingAtControlTestIds.Callouts.FutureDate(),
          text: "If the proposal is signed after ".concat(format(startBillingAt, 'MMM d, yyyy'), ", any charges for prior periods will be invoiced upon approval.")
        });
      }
      if (shouldShowProrateWarning) {
        _callouts.push({
          dataTestid: StartAutomaticBillingAtControlTestIds.Callouts.Proration(),
          text: 'As the selected date doesn’t match the billing day, an additional full invoice will be issued. You can adjust the date or use “first month proration” to align payments.'
        });
      }
      if (periods && periods > 0) {
        _callouts.push({
          dataTestid: StartAutomaticBillingAtControlTestIds.Callouts.PriorPeriods(),
          text: "Invoices for ".concat(periods, " prior ").concat(pluralize('periods', periods), " will be issued once the proposal is approved. If the proposal is signed after ").concat(format(startBillingAt, 'MMM d, yyyy'), ", additional invoices for earlier periods will also be issued.")
        });
      }
    }
    return _callouts;
  }, [selectedItem, isChecked, startBillingAt, shouldShowProrateWarning, periods]);
  return /*#__PURE__*/_jsx(AdvancedSettingsControl, {
    isReadOnly: isReadOnly,
    info: info,
    label: label,
    checked: isChecked,
    testid: StartAutomaticBillingAtControlTestIds.Root(),
    disabled: disabled,
    disabledTooltip: disabledTooltip,
    onChange: checked => {
      if (checked) {
        updateServiceByItem(StartAutomaticBillingAtControlDropdownMenuItem.SpecificDate, getInitialEffectiveDate());
      } else if (selectedItem) {
        updateServiceByItem(selectedItem, false);
      }
    },
    children: isChecked && /*#__PURE__*/_jsxs(Column, {
      className: styles.automaticBillingAtContent,
      gap: 12,
      children: [/*#__PURE__*/_jsxs(Row, {
        className: styles.automaticBillingAtDropdownMenu,
        alignItems: "start",
        children: [/*#__PURE__*/_jsx(Select, {
          size: "small",
          disabled: isReadOnly,
          "data-testid": StartAutomaticBillingAtControlTestIds.DropdownMenu.Root(),
          value: selectedItem,
          onChange: event => {
            const value = event.target.value;
            if (value === StartAutomaticBillingAtControlDropdownMenuItem.SpecificDate) {
              updateServiceByItem(value, getInitialEffectiveDate());
            } else {
              updateServiceByItem(value, true);
            }
          },
          error: !!startBillingAtDropdownError,
          helperTextReserveSpace: true,
          helperText: startBillingAtDropdownError,
          SelectProps: {
            renderValue: value => {
              const selected = menuItems.find(opt => opt.value === value);
              return selected ? selected.text : '';
            }
          },
          children: menuItems.map(item => {
            var _item$tooltip;
            return /*#__PURE__*/_jsx(MenuItem, {
              "data-testid": StartAutomaticBillingAtControlTestIds.DropdownMenu.MenuItem(item.value),
              value: item.value,
              disabled: item.disabled,
              tooltipProps: {
                title: (_item$tooltip = item.tooltip) !== null && _item$tooltip !== void 0 ? _item$tooltip : ''
              },
              variant: "multiline",
              className: styles.menuItem,
              children: /*#__PURE__*/_jsxs(Column, {
                children: [/*#__PURE__*/_jsx(Typography, {
                  weight: "normal",
                  children: item.text
                }), /*#__PURE__*/_jsx(Typography, {
                  children: item.subtext
                })]
              })
            }, item.value);
          })
        }), selectedItem === StartAutomaticBillingAtControlDropdownMenuItem.SpecificDate && /*#__PURE__*/_jsx(KeyboardDatePicker, {
          disabled: isStartBillingAtDateDisabled,
          size: "small",
          minDate: isAmendedService ? amendedMinDate : minDate,
          className: styles.datePicker,
          "data-testid": StartAutomaticBillingAtControlTestIds.StartBillingAtDatePicker(),
          value: startBillingAt,
          error: !!startBillingAtDatePickerError,
          helperText: startBillingAtDatePickerError,
          helperTextReserveSpace: true,
          onChange: date => {
            onStartBillingAtChange === null || onStartBillingAtChange === void 0 || onStartBillingAtChange(service.id, date ? date : null);
          }
        })]
      }), callouts.map(callout => /*#__PURE__*/_jsx(Callout, {
        intent: "warning",
        "data-testid": callout.dataTestid,
        children: callout.text
      }, callout.dataTestid))]
    })
  }, AdvancedSetting.StartAutomaticBillingAt);
};